// C5 — Líneas / Cumplimiento (coordinador).
//
// Rediseño 2026-06: listado de líneas con cumplimiento + estado, base para
// entrar al detalle de cada una.
//   - Header: título "Líneas / Cumplimiento" + segmented período (Hoy/Semana/Mes)
//     + botones Comparar + Exportar
//   - 4 KPI cards SIN barras de progreso (Cumpl. meta · Cumpl. programa ·
//     Producidos perdidos · Paro total → clickeable a /coordinador/analisis-paros)
//   - Filtros: pills de Área + combobox con buscador de SKU (cascada)
//   - Pivot Por línea / Por día / Por SKU (SegmentedControl)
//   - Tabla con headers clickeables (ThSort, nulls al final), sin mini-barras
//   - Click de fila (línea/sku) → /coordinador/analisis-paros?linea_id=X (interim)

import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CoordinadorSidebar } from '../../components/layout/CoordinadorSidebar';
import { RoleLayout } from '../../components/layout/RoleLayout';
import {
  inicioSemanaOperativa,
  type Agrupacion,
  type CumplimientoFila,
  type CumplimientoQuery,
  type Granularidad,
  type ProduccionPorHora,
  type ProduccionPorTurno,
} from 'schemas';
import {
  Bar,
  BarChart,
  Cell,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { EtiquetaValor, pasoEtiquetas } from '../../components/charts/value-labels';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { InfoHint } from '../../components/ui/info-hint';
import { Input } from '../../components/ui/input';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { Spinner } from '../../components/ui/spinner';
import { TBody, TD, TFoot, TH, THead, TR, Table } from '../../components/ui/table';
import { useCoordinadorCumplimiento, useMe } from '../../lib/api/queries';
import { useTenantConfig } from '../../lib/api/useTenantConfig';
import { useAuth } from '../../lib/auth';
import { cn } from '../../lib/utils';
import { semaforoCumplimiento, toneTextClass, type Tone } from '../../lib/semaforo';
import { supabase, API_URL } from '../../lib/supabase';

// =============================================================================
// Tipos locales
// =============================================================================

type Periodo = 'hoy' | 'semana' | 'mes' | 'rango';

/** Vista de la pantalla: operación (cumplimiento) o rentabilidad ($). */
type VistaC5 = 'operacion' | 'rentabilidad';

type SortDir = 'asc' | 'desc';

/** Claves ordenables (superset; cada pivot expone su subconjunto). */
type SortKey =
  | 'periodo'
  | 'linea'
  | 'area'
  | 'producido'
  | 'meta'
  | 'cumpl_meta'
  | 'programa'
  | 'cumpl_prog'
  | 'fuera_plan'
  | 'horas_trab'
  | 'horas_paro'
  | 'lineas';

// =============================================================================
// Textos de ayuda contextual (columnas de tabla)
// =============================================================================

const HINT_COL = {
  producido: 'Paquetes realmente producidos y capturados en el período.',
  meta: 'Capacidad estándar de la línea en las horas trabajadas: lo que debería haber producido a ritmo nominal.',
  pctMeta: 'Producido ÷ Meta. Verde ≥90, ámbar 70-89, rojo <70.',
  programa: 'Paquetes que pidió Alpina para el período (el programa vigente).',
  pctProg: 'Producido ÷ Programación. Qué porcentaje del pedido de Alpina se cumplió.',
  horasTrab:
    'Horas con captura registrada: suma la duración real de cada bloque capturado (los parciales de borde de turno cuentan proporcionalmente).',
  horasParo: 'Horas que la línea estuvo detenida en el período.',
  topCausal: 'Causa de paro con más minutos acumulados en el período.',
} as const;

// =============================================================================
// Helpers
// =============================================================================

function fmtNumero(n: number | null): string {
  // null = dato no atribuible en este filtro (ej. programa por línea, V2).
  if (n === null) return '—';
  return Math.round(n).toLocaleString('es-CO');
}

function fmtPct(n: number | null, decimals = 1): string {
  if (n === null) return '—';
  return `${n.toFixed(decimals)}%`;
}

/** Pesos con separador de miles es-CO. Negativos con signo menos antes del $. */
function fmtCop(n: number | null): string {
  if (n === null) return '—';
  const r = Math.round(n);
  return `${r < 0 ? '−' : ''}$${Math.abs(r).toLocaleString('es-CO')}`;
}

/** Pesos compacto para las tarjetas: "$247,6M", "$87,7K", "−$3,6M". */
function fmtCopCompacto(n: number | null): string {
  if (n === null) return '—';
  const abs = Math.abs(n);
  const signo = n < 0 ? '−' : '';
  const dec = (x: number) => x.toLocaleString('es-CO', { maximumFractionDigits: 1 });
  if (abs >= 1_000_000) return `${signo}$${dec(abs / 1_000_000)}M`;
  if (abs >= 1_000) return `${signo}$${dec(abs / 1_000)}K`;
  return `${signo}$${Math.round(abs).toLocaleString('es-CO')}`;
}

function fmtFechaCorta(iso: string): string {
  if (!iso || iso.length < 10) return iso;
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Intl.DateTimeFormat('es-CO', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })
    .format(new Date(Date.UTC(y, m - 1, d)))
    .replace(/\.|,/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Horas decimales → "112h" o "6h 52m". */
function fmtHoras(horas: number): string {
  const totalMin = Math.round(horas * 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/** Minutos totales → "Xh Ym" (KPI de paro). */
function fmtMinutos(min: number): string {
  const total = Math.round(min);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/**
 * Tono de un delta vs período anterior.
 * @param betterWhenUp si true, subir es bueno (cumplimiento). Si false, subir es
 *   malo (producidos perdidos, # combinaciones bajo meta).
 */
function toneDelta(delta: number | null, betterWhenUp: boolean): Tone {
  if (delta === null || delta === 0) return 'neutral';
  const sube = delta > 0;
  const bueno = betterWhenUp ? sube : !sube;
  return bueno ? 'ok' : 'crit';
}

function toneDotClass(tone: Tone): string {
  return tone === 'ok'
    ? 'bg-ok'
    : tone === 'warn'
      ? 'bg-warn'
      : tone === 'crit'
        ? 'bg-crit'
        : 'bg-fg-tertiary';
}

/** Fecha local YYYY-MM-DD. */
function ymd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Período → {desde, hasta, granularidad de período}. La semana es la
 *  operativa del tenant (default lunes). */
function periodoAFiltros(
  periodo: Periodo,
  diaInicioSemana = 1,
): {
  desde: string;
  hasta: string;
  granularidad: 'dia' | 'semana' | 'mes';
} {
  const hoy = new Date();
  const hastaStr = ymd(hoy);
  if (periodo === 'hoy') {
    return { desde: hastaStr, hasta: hastaStr, granularidad: 'dia' };
  }
  if (periodo === 'semana') {
    return {
      desde: inicioSemanaOperativa(hastaStr, diaInicioSemana),
      hasta: hastaStr,
      granularidad: 'semana',
    };
  }
  // mes
  const primerDia = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  return { desde: ymd(primerDia), hasta: hastaStr, granularidad: 'mes' };
}

// =============================================================================
// Componente principal
// =============================================================================

export function C5AnalisisCumplimientoPage() {
  const { state, signOut } = useAuth();

  const [periodo, setPeriodo] = useState<Periodo>('semana');
  const [agrupacion, setAgrupacion] = useState<Agrupacion>('area');
  const [areaId, setAreaId] = useState<string | undefined>(undefined);
  const [lineaId, setLineaId] = useState<string | undefined>(undefined);
  const [skuId, setSkuId] = useState<string | undefined>(undefined);
  // Filtro por turno (D11, paridad con la foto S9 del supervisor). El cruce vs
  // programa es diario → turno no combina con el pivot 'area': al elegir turno
  // se conmuta a 'linea', y al volver a 'area' se limpia el turno.
  const [turnoNumero, setTurnoNumero] = useState<number | undefined>(undefined);
  const [comparar, setComparar] = useState(false);
  const [vista, setVista] = useState<VistaC5>('operacion');

  const onTurno = (n: number | undefined) => {
    setTurnoNumero(n);
    if (n != null && agrupacion === 'area') setAgrupacion('linea');
  };
  const onAgrupacionSafe = (a: Agrupacion) => {
    if (a === 'area' && turnoNumero != null) setTurnoNumero(undefined);
    setAgrupacion(a);
  };
  // Granularidad del gráfico de rentabilidad en el tiempo (default: por día).
  const [granRent, setGranRent] = useState<Granularidad>('dia');

  const me = useMe();
  const { diaInicioSemana } = useTenantConfig();
  const puedeVerCostos = me.data?.ctx.puede_ver_costos ?? false;
  // Si no puede ver costos, la vista de rentabilidad no está disponible.
  const vistaEfectiva: VistaC5 = puedeVerCostos ? vista : 'operacion';

  // Rango personalizado (solo activo cuando periodo === 'rango'). Default: últimos
  // 7 días (hoy-6 → hoy) para que los inputs arranquen con algo sensato.
  const [rango, setRango] = useState<{ desde: string; hasta: string }>(() => {
    const hoy = new Date();
    const inicio = new Date(hoy);
    inicio.setDate(hoy.getDate() - 6);
    return { desde: ymd(inicio), hasta: ymd(hoy) };
  });

  // Rango válido solo si ambas fechas presentes y desde ≤ hasta.
  const rangoValido = Boolean(rango.desde && rango.hasta && rango.desde <= rango.hasta);

  // Período + pivot → filtros enviados al endpoint.
  // REGLA: pivot 'dia' fuerza granularidad='dia' (filas por día) manteniendo la
  // ventana desde/hasta del período. 'linea' y 'sku' usan la granularidad del
  // período (una fila agregada por línea/sku sobre la ventana).
  // Para 'rango': el backend agrega por dimensión sobre cualquier ventana, así
  // que mandamos granularidad='dia' (indistinto para línea/sku). Si el rango es
  // inválido NO recalculamos (la query mantiene su key anterior y no se dispara).
  const filtros: CumplimientoQuery = useMemo(() => {
    // En Rentabilidad, el control Día/Semana/Mes del gráfico manda la granularidad.
    if (periodo === 'rango') {
      return {
        desde: rango.desde,
        hasta: rango.hasta,
        granularidad: vistaEfectiva === 'rentabilidad' ? granRent : 'dia',
        agrupacion,
        area_id: areaId,
        linea_id: lineaId,
        sku_id: skuId,
        turno_numero: turnoNumero,
        comparar,
      };
    }
    const { desde, hasta, granularidad } = periodoAFiltros(periodo, diaInicioSemana);
    return {
      desde,
      hasta,
      granularidad:
        vistaEfectiva === 'rentabilidad' ? granRent : agrupacion === 'dia' ? 'dia' : granularidad,
      agrupacion,
      area_id: areaId,
      linea_id: lineaId,
      sku_id: skuId,
      turno_numero: turnoNumero,
      comparar,
    };
  }, [
    periodo,
    rango.desde,
    rango.hasta,
    agrupacion,
    areaId,
    lineaId,
    skuId,
    turnoNumero,
    comparar,
    vistaEfectiva,
    granRent,
    diaInicioSemana,
  ]);

  // Si el rango es inválido, congelamos los filtros en su último valor válido
  // (no disparamos query con un rango incoherente).
  const filtrosCommitRef = useRef<CumplimientoQuery>(filtros);
  if (periodo !== 'rango' || rangoValido) {
    filtrosCommitRef.current = filtros;
  }
  const filtrosEfectivos = filtrosCommitRef.current;

  const { data, isLoading, isError, error, refetch, isFetching } =
    useCoordinadorCumplimiento(filtrosEfectivos);

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  if (isLoading) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 p-6">
        <Spinner className="size-6" />
        <p className="text-sm text-fg-tertiary">Cargando análisis…</p>
      </main>
    );
  }

  if (isError || !data) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 p-6 text-center">
        <h1 className="text-lg font-medium text-crit">No pudimos cargar</h1>
        <p className="text-sm text-fg-secondary">
          {error instanceof Error ? error.message : 'Error desconocido'}
        </p>
        <Button onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? 'Reintentando…' : 'Reintentar'}
        </Button>
        <button onClick={() => signOut()} className="text-xs text-fg-tertiary underline">
          Cerrar sesión
        </button>
      </main>
    );
  }

  return (
    <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel="Coordinador">
      <div className="flex flex-col gap-4">
        <HeaderRibbon
          data={data}
          periodo={periodo}
          onPeriodo={setPeriodo}
          rango={rango}
          onRango={setRango}
          rangoValido={rangoValido}
          comparar={comparar}
          onToggleComparar={() => setComparar((c) => !c)}
        />

        {puedeVerCostos ? <VistaToggle vista={vistaEfectiva} onChange={setVista} /> : null}

        {vistaEfectiva === 'rentabilidad' ? (
          <KpiCardsRentabilidad data={data} />
        ) : (
          <KpiCardsRow data={data} comparar={comparar} />
        )}

        {vistaEfectiva === 'rentabilidad' ? (
          <BannerPerdida data={data} agrupacion={agrupacion} />
        ) : null}

        <FiltrosBar
          data={data}
          areaId={areaId}
          lineaId={lineaId}
          skuId={skuId}
          turnoNumero={turnoNumero}
          onArea={(id) => {
            setAreaId(id);
            // limpiar línea/sku si dejan de pertenecer al área elegida
            setLineaId(undefined);
            setSkuId(undefined);
          }}
          onSku={setSkuId}
          onTurno={onTurno}
        />

        {vistaEfectiva === 'rentabilidad' ? (
          <>
            <TablaRentabilidad
              data={data}
              agrupacion={agrupacion}
              onAgrupacion={onAgrupacionSafe}
            />
            <TablaRentabilidadTiendas data={data} />
          </>
        ) : (
          <TablaLineas data={data} agrupacion={agrupacion} onAgrupacion={onAgrupacionSafe} />
        )}

        {vistaEfectiva !== 'rentabilidad' ? (
          <IntensidadOperacionSection
            porTurno={data.produccion_por_turno}
            porHora={data.produccion_por_hora}
          />
        ) : null}

        {vistaEfectiva === 'rentabilidad' ? (
          <>
            <GraficoRentabilidadTiempo data={data} granRent={granRent} onGranRent={setGranRent} />
            <GraficoUtilidadArea data={data} />
          </>
        ) : null}

        <footer className="text-center text-[11px] text-fg-tertiary">
          C5 · {data.filas.length} fila{data.filas.length === 1 ? '' : 's'} ·{' '}
          {data.totales.dias_capturados} día{data.totales.dias_capturados === 1 ? '' : 's'} con
          datos
        </footer>
      </div>
    </RoleLayout>
  );
}

// =============================================================================
// HeaderRibbon
// =============================================================================

function HeaderRibbon({
  data,
  periodo,
  onPeriodo,
  rango,
  onRango,
  rangoValido,
  comparar,
  onToggleComparar,
}: {
  data: NonNullable<ReturnType<typeof useCoordinadorCumplimiento>['data']>;
  periodo: Periodo;
  onPeriodo: (p: Periodo) => void;
  rango: { desde: string; hasta: string };
  onRango: (r: { desde: string; hasta: string }) => void;
  rangoValido: boolean;
  comparar: boolean;
  onToggleComparar: () => void;
}) {
  const plantaLabel = data.scope.plantas_asignadas.map((p) => p.nombre).join(' · ') || 'Sopó';

  const exportar = async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) {
        toast.error('Sesión expirada');
        return;
      }
      const params = new URLSearchParams();
      if (data.filtros.desde) params.set('desde', data.filtros.desde);
      if (data.filtros.hasta) params.set('hasta', data.filtros.hasta);
      if (data.filtros.granularidad) params.set('granularidad', data.filtros.granularidad);
      if (data.filtros.area_id) params.set('area_id', data.filtros.area_id);
      if (data.filtros.linea_id) params.set('linea_id', data.filtros.linea_id);
      if (data.filtros.sku_id) params.set('sku_id', data.filtros.sku_id);
      // Mismo turno que la pantalla — un export con todos los turnos contradiciendo
      // la vista filtrada sería otra incoherencia (D11).
      if (data.filtros.turno_numero != null) {
        params.set('turno_numero', String(data.filtros.turno_numero));
      }
      const qs = params.toString();
      const res = await fetch(
        `${API_URL}/api/coordinador/cumplimiento/export${qs ? `?${qs}` : ''}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        toast.error(`Error ${res.status}: no se pudo exportar`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cumplimiento_hora_a_hora_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Excel descargado');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error');
    }
  };

  return (
    <header className="flex flex-wrap items-end justify-between gap-3 border-b border-border pb-3">
      <div>
        <h1 className="text-xl font-medium text-fg">Líneas / Cumplimiento</h1>
        <p className="mt-1 text-[11px] text-fg-tertiary">
          {plantaLabel} · seleccione una línea para ver su detalle
        </p>
      </div>
      <div className="flex flex-col items-end gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <SegmentedControl<Periodo>
            size="sm"
            value={periodo}
            onChange={onPeriodo}
            options={[
              { value: 'hoy', label: 'Hoy' },
              { value: 'semana', label: 'Esta semana' },
              { value: 'mes', label: 'Este mes' },
              { value: 'rango', label: 'Rango' },
            ]}
          />
          <Button variant="outline" size="sm" onClick={onToggleComparar}>
            {comparar ? '✓ Comparando' : 'Comparar'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportar}
            title="Descarga el detalle bloque a bloque (turno · bloque · SKU · paros). El cumplimiento es vs meta de capacidad (eficiencia por hora), no vs programa diario."
          >
            ↓ Exportar
          </Button>
        </div>
        {periodo === 'rango' ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                Desde
              </span>
              <Input
                type="date"
                value={rango.desde}
                max={rango.hasta || undefined}
                onChange={(e) => onRango({ ...rango, desde: e.target.value })}
                className="h-8 w-[150px]"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                Hasta
              </span>
              <Input
                type="date"
                value={rango.hasta}
                min={rango.desde || undefined}
                onChange={(e) => onRango({ ...rango, hasta: e.target.value })}
                className="h-8 w-[150px]"
              />
            </div>
            {!rangoValido ? (
              <span className="text-[11px] text-warn">La fecha "Desde" debe ser ≤ "Hasta".</span>
            ) : null}
          </div>
        ) : null}
      </div>
    </header>
  );
}

// =============================================================================
// KpiCardsRow — 4 cards SIN barras de progreso
// =============================================================================

function KpiCardsRow({
  data,
  comparar,
}: {
  data: NonNullable<ReturnType<typeof useCoordinadorCumplimiento>['data']>;
  comparar: boolean;
}) {
  const navigate = useNavigate();
  const { totales } = data;
  const comparativo = data.comparativo;

  // Solo mostramos deltas si el toggle está activo. Si está activo pero el
  // backend no devolvió comparativo (sin período anterior), mostramos un hint.
  const mostrarDeltas = comparar && comparativo !== null;
  const sinDatosAnteriores = comparar && comparativo === null;

  // Rango del período anterior, p.ej. "26 may–1 jun".
  const rangoAnterior: string | undefined = comparativo
    ? `${fmtFechaCorta(comparativo.desde)}–${fmtFechaCorta(comparativo.hasta)}`
    : undefined;

  return (
    <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <KpiCard
        label="Cumpl. meta"
        hint="Cuánto produjo la línea frente a su capacidad estándar, en las horas que trabajó. 100% = rindió a su ritmo nominal. No depende del pedido de Alpina."
        value={fmtPct(totales.cumplimiento_meta_promedio_pct)}
        tone={semaforoCumplimiento(totales.cumplimiento_meta_promedio_pct)}
        sub={`${fmtNumero(totales.cantidad_real_total)} / ${fmtNumero(totales.meta_acumulada_total)} paq`}
        delta={
          mostrarDeltas
            ? deltaPp(comparativo.delta_cumplimiento_meta_pct, rangoAnterior)
            : undefined
        }
        sinComparable={sinDatosAnteriores}
      />
      <KpiCard
        label="Cumpl. programa"
        hint="Cuánto se produjo frente a lo que pidió Alpina, medido POR ÁREA (proceso): la producción se mueve entre líneas de un área, así que el cumplimiento se reconcilia a nivel área, no por línea. 100% = se cumplió el pedido. La producción de SKUs no programados se reporta aparte como 'fuera de plan'."
        value={fmtPct(totales.cumplimiento_promedio_pct)}
        tone={semaforoCumplimiento(totales.cumplimiento_promedio_pct)}
        sub={
          totales.programa_por_linea_no_disponible
            ? 'no disponible con filtro de línea — el programa se carga por área'
            : `${fmtNumero(totales.cantidad_programa_total)} paq · ${fmtNumero(totales.producido_fuera_plan_total)} fuera de plan`
        }
        delta={
          mostrarDeltas ? deltaPp(comparativo.delta_cumplimiento_pct, rangoAnterior) : undefined
        }
        sinComparable={sinDatosAnteriores}
      />
      <KpiCard
        label="Producidos perdidos"
        hint="Paquetes que la línea podía haber producido a su ritmo estándar en las horas trabajadas, y no produjo. Es brecha de ritmo: no cuenta las horas que no se trabajaron."
        value={fmtNumero(totales.producidos_perdidos_total)}
        valueSuffix=" paq"
        tone="warn"
        sub="meta − producido"
        delta={
          mostrarDeltas
            ? deltaAbs(comparativo.delta_producidos_perdidos, 'paq', false, rangoAnterior)
            : undefined
        }
        sinComparable={sinDatosAnteriores}
      />
      <KpiCard
        label="Paro total"
        hint="Tiempo total que las líneas estuvieron detenidas en el período. El desglose por causa está en Análisis de paros."
        value={fmtMinutos(totales.minutos_paro_total)}
        tone={totales.minutos_paro_total > 0 ? 'crit' : 'neutral'}
        sub={
          totales.bajo_bajo_meta_count > 0
            ? `${totales.bajo_bajo_meta_count} combinación${totales.bajo_bajo_meta_count === 1 ? '' : 'es'} crítica${totales.bajo_bajo_meta_count === 1 ? '' : 's'}`
            : 'ver análisis de paros'
        }
        delta={
          mostrarDeltas
            ? deltaAbs(comparativo.delta_bajo_bajo_meta_count, 'críticas', false, rangoAnterior)
            : undefined
        }
        sinComparable={sinDatosAnteriores}
        onClick={() => navigate('/coordinador/analisis-paros')}
      />
    </section>
  );
}

/** Descriptor de delta para una KPI card (texto + tono ya resueltos). */
type DeltaInfo = { text: string; tone: Tone };

/** Delta en puntos porcentuales (cumplimiento): subir es bueno. */
function deltaPp(delta: number | null, rangoAnterior: string | undefined): DeltaInfo {
  if (delta === null) {
    return { text: 'sin comparable', tone: 'neutral' };
  }
  const tone = toneDelta(delta, true);
  const flecha = delta > 0 ? '▲' : delta < 0 ? '▼' : '→';
  const signo = delta > 0 ? '+' : '';
  const vs = rangoAnterior ? ` vs ${rangoAnterior}` : ' vs período anterior';
  return { text: `${flecha} ${signo}${delta.toFixed(1)} pp${vs}`, tone };
}

/**
 * Delta absoluto (producidos perdidos, # combinaciones). `betterWhenUp=false`:
 * subir es MALO → rojo.
 */
function deltaAbs(
  delta: number,
  unidad: string,
  betterWhenUp: boolean,
  rangoAnterior: string | undefined,
): DeltaInfo {
  const tone = toneDelta(delta, betterWhenUp);
  const flecha = delta > 0 ? '▲' : delta < 0 ? '▼' : '→';
  const signo = delta > 0 ? '+' : delta < 0 ? '−' : '';
  const vs = rangoAnterior ? ` vs ${rangoAnterior}` : ' vs período anterior';
  return { text: `${flecha} ${signo}${fmtNumero(Math.abs(delta))} ${unidad}${vs}`, tone };
}

function KpiCard({
  label,
  hint,
  value,
  valueSuffix,
  tone,
  sub,
  delta,
  sinComparable,
  onClick,
}: {
  label: string;
  /** Explicación llana de la métrica (ⓘ al lado del label). */
  hint?: React.ReactNode;
  value: string;
  valueSuffix?: string;
  tone: Tone;
  sub: string;
  /** Sub-renglón de delta vs período anterior (solo cuando "Comparar" activo). */
  delta?: DeltaInfo;
  /** "Comparar" activo pero sin datos del período anterior. */
  sinComparable?: boolean;
  onClick?: () => void;
}) {
  const clickable = !!onClick;
  return (
    <Card
      className={cn(clickable && 'cursor-pointer transition-colors hover:border-fg-tertiary')}
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
    >
      <CardContent className="flex flex-col gap-2 p-3">
        <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
          {label}
          {hint ? (
            // El ⓘ no debe disparar el onClick de la card (ej. "Paro total" navega).
            <span
              className="inline-flex"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <InfoHint label={label}>{hint}</InfoHint>
            </span>
          ) : null}
        </span>
        <span
          className={cn(
            'font-semibold tabular-nums tracking-tight text-[28px] leading-none',
            toneTextClass(tone) === 'text-fg-tertiary' ? 'text-fg' : toneTextClass(tone),
          )}
        >
          {value}
          {valueSuffix ? <span className="text-[14px] text-fg-tertiary">{valueSuffix}</span> : null}
        </span>
        <p className="text-[10.5px] text-fg-tertiary">{sub}</p>
        {delta ? (
          <p className={cn('text-[10.5px] font-medium tabular-nums', toneTextClass(delta.tone))}>
            {delta.text}
          </p>
        ) : sinComparable ? (
          <p className="text-[10.5px] text-fg-tertiary">
            Sin datos del período anterior para comparar.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// FiltrosBar — pills de Área + combobox con buscador de SKU
// =============================================================================

function FiltrosBar({
  data,
  areaId,
  lineaId,
  skuId,
  turnoNumero,
  onArea,
  onSku,
  onTurno,
}: {
  data: NonNullable<ReturnType<typeof useCoordinadorCumplimiento>['data']>;
  areaId: string | undefined;
  lineaId: string | undefined;
  skuId: string | undefined;
  turnoNumero: number | undefined;
  onArea: (id: string | undefined) => void;
  onSku: (id: string | undefined) => void;
  onTurno: (n: number | undefined) => void;
}) {
  // SKUs en cascada: por línea (si hay) o por área (intersección de líneas).
  const skusFiltrados = useMemo(() => {
    if (lineaId) {
      return data.recursos.skus.filter((s) => s.linea_ids.includes(lineaId));
    }
    if (areaId) {
      const lineasArea = new Set(
        data.recursos.lineas.filter((l) => l.area_id === areaId).map((l) => l.id),
      );
      return data.recursos.skus.filter((s) => s.linea_ids.some((id) => lineasArea.has(id)));
    }
    return data.recursos.skus;
  }, [data.recursos.skus, data.recursos.lineas, areaId, lineaId]);

  return (
    <section className="flex flex-col gap-3 rounded-md border border-border bg-surface-overlay/40 p-3">
      <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
        <FiltroBloque label="Área">
          <div className="flex flex-wrap gap-1" role="radiogroup">
            <AreaPill activo={!areaId} onClick={() => onArea(undefined)}>
              Todas
            </AreaPill>
            {data.recursos.areas.map((a) => (
              <AreaPill key={a.id} activo={areaId === a.id} onClick={() => onArea(a.id)}>
                {a.nombre}
              </AreaPill>
            ))}
          </div>
        </FiltroBloque>

        <FiltroBloque label="SKU">
          <SkuCombobox skus={skusFiltrados} value={skuId} onChange={onSku} />
        </FiltroBloque>

        {data.recursos.turnos.length > 0 ? (
          <FiltroBloque label="Turno">
            <div className="flex flex-wrap items-center gap-1" role="radiogroup">
              <AreaPill activo={turnoNumero == null} onClick={() => onTurno(undefined)}>
                Todos
              </AreaPill>
              {data.recursos.turnos.map((t) => (
                <AreaPill
                  key={t.numero}
                  activo={turnoNumero === t.numero}
                  onClick={() => onTurno(t.numero)}
                >
                  {`T${t.numero}`}
                </AreaPill>
              ))}
              <InfoHint>
                Filtra la producción por turno (mismo eje que la foto del supervisor). Con turno
                seleccionado el cumplimiento se mide solo contra capacidad: el programa del cliente
                es diario, no por turno.
              </InfoHint>
            </div>
          </FiltroBloque>
        ) : null}
      </div>
    </section>
  );
}

function FiltroBloque({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
        {label}
      </span>
      {children}
    </div>
  );
}

function AreaPill({
  activo,
  onClick,
  children,
}: {
  activo: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={activo}
      onClick={onClick}
      className={cn(
        'rounded-md border px-2.5 py-1 text-[11.5px] font-medium transition-colors',
        activo
          ? 'border-accent bg-accent text-accent-ink'
          : 'border-border bg-surface-base text-fg-secondary hover:border-fg-tertiary',
      )}
    >
      {children}
    </button>
  );
}

function SkuCombobox({
  skus,
  value,
  onChange,
}: {
  skus: NonNullable<ReturnType<typeof useCoordinadorCumplimiento>['data']>['recursos']['skus'];
  value: string | undefined;
  onChange: (id: string | undefined) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const seleccionado = value ? skus.find((s) => s.id === value) : undefined;

  // Cerrar al hacer click afuera.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const q = query.trim().toLowerCase();
  const opciones = useMemo(() => {
    if (!q) return skus.slice(0, 50);
    return skus
      .filter(
        (s) =>
          s.codigo_cliente.toLowerCase().includes(q) || s.descripcion.toLowerCase().includes(q),
      )
      .slice(0, 50);
  }, [skus, q]);

  const elegir = (id: string | undefined) => {
    onChange(id);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={containerRef} className="relative min-w-[260px]">
      <Input
        type="text"
        value={
          open
            ? query
            : seleccionado
              ? `${seleccionado.codigo_cliente} - ${seleccionado.descripcion}`
              : query
        }
        placeholder={`Buscar SKU… (${skus.length})`}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!open) setOpen(true);
        }}
        className="w-full"
      />
      {open ? (
        <div className="absolute left-0 right-0 top-[calc(100%+2px)] z-20 max-h-64 overflow-y-auto rounded-md border border-border bg-surface-elevated shadow-lg">
          <button
            type="button"
            onClick={() => elegir(undefined)}
            className={cn(
              'block w-full px-2.5 py-1.5 text-left text-[11.5px] hover:bg-surface-overlay',
              !value ? 'font-medium text-fg' : 'text-fg-secondary',
            )}
          >
            Todos ({skus.length})
          </button>
          {opciones.length === 0 ? (
            <p className="px-2.5 py-2 text-[11px] text-fg-tertiary">Sin coincidencias</p>
          ) : (
            opciones.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => elegir(s.id)}
                className={cn(
                  'block w-full px-2.5 py-1.5 text-left text-[11.5px] hover:bg-surface-overlay',
                  value === s.id ? 'font-medium text-fg' : 'text-fg-secondary',
                )}
              >
                <span className="font-medium text-fg">{s.codigo_cliente}</span>
                <span className="text-fg-tertiary"> - {s.descripcion}</span>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

// =============================================================================
// TablaLineas — pivot + headers clickeables (ThSort), sin mini-barras
// =============================================================================

function TablaLineas({
  data,
  agrupacion,
  onAgrupacion,
}: {
  data: NonNullable<ReturnType<typeof useCoordinadorCumplimiento>['data']>;
  agrupacion: Agrupacion;
  onAgrupacion: (a: Agrupacion) => void;
}) {
  const navigate = useNavigate();

  // Orden por defecto: peor % meta arriba (asc, nulls al final).
  const [sortKey, setSortKey] = useState<SortKey>('cumpl_meta');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const agrupacionActual: Agrupacion = data.filtros.agrupacion;

  const valorOrden = (f: CumplimientoFila, key: SortKey): number | string | null => {
    switch (key) {
      case 'periodo':
        return f.periodo;
      case 'linea':
        return f.linea_codigo;
      case 'area':
        return f.area_nombre;
      case 'producido':
        return f.cantidad_real;
      case 'meta':
        return f.meta_acumulada;
      case 'cumpl_meta':
        return f.cumplimiento_meta_pct;
      case 'programa':
        return f.cantidad_programa;
      case 'cumpl_prog':
        return f.cumplimiento_pct;
      case 'fuera_plan':
        return f.producido_fuera_plan;
      case 'horas_trab':
        return f.horas_trabajadas;
      case 'horas_paro':
        return f.horas_paro;
      case 'lineas':
        return f.lineas_count;
      default:
        return null;
    }
  };

  const filasOrdenadas = useMemo(() => {
    const sorted = [...data.filas];
    sorted.sort((a, b) => {
      const av = valorOrden(a, sortKey);
      const bv = valorOrden(b, sortKey);
      // nulls siempre al final, independiente de la dirección
      const aNull = av === null;
      const bNull = bv === null;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      let diff: number;
      if (typeof av === 'string' && typeof bv === 'string') {
        diff = av.localeCompare(bv, 'es');
      } else {
        diff = (av as number) - (bv as number);
      }
      return sortDir === 'asc' ? diff : -diff;
    });
    return sorted;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.filas, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const onClickLinea = (lid: string) => {
    // Llevar el período/granularidad actual a la pantalla "Línea en el tiempo".
    const params = new URLSearchParams();
    if (data.filtros.desde) params.set('desde', data.filtros.desde);
    if (data.filtros.hasta) params.set('hasta', data.filtros.hasta);
    if (data.filtros.granularidad) params.set('granularidad', data.filtros.granularidad);
    const qs = params.toString();
    navigate(`/coordinador/linea/${lid}${qs ? `?${qs}` : ''}`);
  };

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <h2 className="text-sm font-medium text-fg">Líneas</h2>
            <p className="text-[11px] text-fg-tertiary">
              <span className="tabular-nums">{data.filas.length}</span>{' '}
              {agrupacionActual === 'area'
                ? `área${data.filas.length === 1 ? '' : 's'}`
                : agrupacionActual === 'linea'
                  ? `línea${data.filas.length === 1 ? '' : 's'}`
                  : agrupacionActual === 'sku'
                    ? `SKU${data.filas.length === 1 ? '' : 's'}`
                    : `día${data.filas.length === 1 ? '' : 's'}`}
            </p>
          </div>
          <SegmentedControl<Agrupacion>
            size="sm"
            value={agrupacion}
            onChange={onAgrupacion}
            options={[
              { value: 'area', label: 'Por área' },
              { value: 'linea', label: 'Por línea' },
              { value: 'dia', label: 'Por día' },
              { value: 'sku', label: 'Por SKU' },
            ]}
          />
        </div>

        {filasOrdenadas.length === 0 ? (
          <p className="py-4 text-center text-sm text-fg-tertiary">
            Sin datos en el período seleccionado.
          </p>
        ) : (
          <Table maxHeight="70vh">
            <THead sticky>
              {agrupacionActual === 'area' ? (
                <TR>
                  <ThSort
                    active={sortKey === 'area'}
                    dir={sortDir}
                    onClick={() => toggleSort('area')}
                  >
                    Área
                  </ThSort>
                  <ThSort
                    active={sortKey === 'producido'}
                    dir={sortDir}
                    onClick={() => toggleSort('producido')}
                    align="right"
                    hint={HINT_COL.producido}
                  >
                    Producido
                  </ThSort>
                  <ThSort
                    active={sortKey === 'programa'}
                    dir={sortDir}
                    onClick={() => toggleSort('programa')}
                    align="right"
                    hint={HINT_COL.programa}
                  >
                    Programación
                  </ThSort>
                  <ThSort
                    active={sortKey === 'cumpl_prog'}
                    dir={sortDir}
                    onClick={() => toggleSort('cumpl_prog')}
                    align="right"
                    hint={HINT_COL.pctProg}
                  >
                    % prog
                  </ThSort>
                  <ThSort
                    active={sortKey === 'fuera_plan'}
                    dir={sortDir}
                    onClick={() => toggleSort('fuera_plan')}
                    align="right"
                    hint="Producción real de SKUs que NO estaban programados en esta área."
                  >
                    Fuera de plan
                  </ThSort>
                  <ThSort
                    active={sortKey === 'cumpl_meta'}
                    dir={sortDir}
                    onClick={() => toggleSort('cumpl_meta')}
                    align="right"
                    hint={HINT_COL.pctMeta}
                  >
                    % capacidad
                  </ThSort>
                </TR>
              ) : agrupacionActual === 'linea' ? (
                <TR>
                  <ThSort
                    active={sortKey === 'linea'}
                    dir={sortDir}
                    onClick={() => toggleSort('linea')}
                  >
                    Línea
                  </ThSort>
                  <ThSort
                    active={sortKey === 'producido'}
                    dir={sortDir}
                    onClick={() => toggleSort('producido')}
                    align="right"
                    hint={HINT_COL.producido}
                  >
                    Producido
                  </ThSort>
                  <ThSort
                    active={sortKey === 'meta'}
                    dir={sortDir}
                    onClick={() => toggleSort('meta')}
                    align="right"
                    hint={HINT_COL.meta}
                  >
                    Meta
                  </ThSort>
                  <ThSort
                    active={sortKey === 'cumpl_meta'}
                    dir={sortDir}
                    onClick={() => toggleSort('cumpl_meta')}
                    align="right"
                    hint={HINT_COL.pctMeta}
                  >
                    % meta
                  </ThSort>
                  <ThSort
                    active={sortKey === 'horas_trab'}
                    dir={sortDir}
                    onClick={() => toggleSort('horas_trab')}
                    align="right"
                    hint={HINT_COL.horasTrab}
                  >
                    Horas trab.
                  </ThSort>
                  <ThSort
                    active={sortKey === 'horas_paro'}
                    dir={sortDir}
                    onClick={() => toggleSort('horas_paro')}
                    align="right"
                    hint={HINT_COL.horasParo}
                  >
                    Horas paro
                  </ThSort>
                  <TH>
                    <span className="inline-flex items-center gap-1">
                      Top causal
                      <InfoHint>{HINT_COL.topCausal}</InfoHint>
                    </span>
                  </TH>
                  <TH align="right" />
                </TR>
              ) : agrupacionActual === 'dia' ? (
                <TR>
                  <ThSort
                    active={sortKey === 'periodo'}
                    dir={sortDir}
                    onClick={() => toggleSort('periodo')}
                  >
                    Día
                  </ThSort>
                  <ThSort
                    active={sortKey === 'producido'}
                    dir={sortDir}
                    onClick={() => toggleSort('producido')}
                    align="right"
                    hint={HINT_COL.producido}
                  >
                    Producido
                  </ThSort>
                  <ThSort
                    active={sortKey === 'meta'}
                    dir={sortDir}
                    onClick={() => toggleSort('meta')}
                    align="right"
                    hint={HINT_COL.meta}
                  >
                    Meta
                  </ThSort>
                  <ThSort
                    active={sortKey === 'cumpl_meta'}
                    dir={sortDir}
                    onClick={() => toggleSort('cumpl_meta')}
                    align="right"
                    hint={HINT_COL.pctMeta}
                  >
                    % meta
                  </ThSort>
                  <ThSort
                    active={sortKey === 'programa'}
                    dir={sortDir}
                    onClick={() => toggleSort('programa')}
                    align="right"
                    hint={HINT_COL.programa}
                  >
                    Programación
                  </ThSort>
                  <ThSort
                    active={sortKey === 'cumpl_prog'}
                    dir={sortDir}
                    onClick={() => toggleSort('cumpl_prog')}
                    align="right"
                    hint={HINT_COL.pctProg}
                  >
                    % prog
                  </ThSort>
                  <ThSort
                    active={sortKey === 'horas_paro'}
                    dir={sortDir}
                    onClick={() => toggleSort('horas_paro')}
                    align="right"
                    hint={HINT_COL.horasParo}
                  >
                    Horas paro
                  </ThSort>
                  <ThSort
                    active={sortKey === 'lineas'}
                    dir={sortDir}
                    onClick={() => toggleSort('lineas')}
                    align="right"
                  >
                    Líneas
                  </ThSort>
                  <TH>
                    <span className="inline-flex items-center gap-1">
                      Top causal
                      <InfoHint>{HINT_COL.topCausal}</InfoHint>
                    </span>
                  </TH>
                </TR>
              ) : (
                <TR>
                  <ThSort
                    active={sortKey === 'linea'}
                    dir={sortDir}
                    onClick={() => toggleSort('linea')}
                  >
                    SKU
                  </ThSort>
                  <ThSort
                    active={sortKey === 'producido'}
                    dir={sortDir}
                    onClick={() => toggleSort('producido')}
                    align="right"
                    hint={HINT_COL.producido}
                  >
                    Producido
                  </ThSort>
                  <ThSort
                    active={sortKey === 'meta'}
                    dir={sortDir}
                    onClick={() => toggleSort('meta')}
                    align="right"
                    hint={HINT_COL.meta}
                  >
                    Meta
                  </ThSort>
                  <ThSort
                    active={sortKey === 'cumpl_meta'}
                    dir={sortDir}
                    onClick={() => toggleSort('cumpl_meta')}
                    align="right"
                    hint={HINT_COL.pctMeta}
                  >
                    % meta
                  </ThSort>
                  <ThSort
                    active={sortKey === 'programa'}
                    dir={sortDir}
                    onClick={() => toggleSort('programa')}
                    align="right"
                    hint={HINT_COL.programa}
                  >
                    Programación
                  </ThSort>
                  <ThSort
                    active={sortKey === 'cumpl_prog'}
                    dir={sortDir}
                    onClick={() => toggleSort('cumpl_prog')}
                    align="right"
                    hint={HINT_COL.pctProg}
                  >
                    % prog
                  </ThSort>
                  <ThSort
                    active={sortKey === 'lineas'}
                    dir={sortDir}
                    onClick={() => toggleSort('lineas')}
                    align="right"
                  >
                    Líneas
                  </ThSort>
                </TR>
              )}
            </THead>
            <TBody>
              {filasOrdenadas.map((f, i) => (
                <FilaLinea
                  key={`${f.periodo}-${f.linea_id}-${f.sku_id ?? ''}-${i}`}
                  fila={f}
                  agrupacion={agrupacionActual}
                  onClickLinea={onClickLinea}
                />
              ))}
            </TBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// IntensidadOperacionSection — producción por turno + perfil por hora, debajo
// de TablaLineas en la vista "operación". Barras = cantidad producida, línea
// = % cumplimiento vs. meta (capacidad estándar). Mismos filtros que la tabla
// (área/línea/sku/turno/período) — vienen ya agregados por el backend con el
// WHERE del bloque principal.
// =============================================================================

function IntensidadOperacionSection({
  porTurno,
  porHora,
}: {
  porTurno: ProduccionPorTurno[];
  porHora: ProduccionPorHora[];
}) {
  const turnoData = useMemo(
    () =>
      porTurno.map((t) => ({
        label: t.turno_nombre || `T${t.turno_numero}`,
        cantidad: Math.round(t.cantidad_real),
        cumplimiento_pct: t.cumplimiento_meta_pct,
      })),
    [porTurno],
  );

  const horaData = useMemo(
    () =>
      porHora.map((h) => ({
        label: `${String(h.hora).padStart(2, '0')}:00`,
        cantidad: Math.round(h.cantidad_real),
        cumplimiento_pct: h.cumplimiento_meta_pct,
      })),
    [porHora],
  );

  const tooltipFormatter = (value: unknown, name: unknown) => {
    if (value === null || value === undefined) return ['s/d', name as string];
    if (name === 'Cumplimiento vs. meta') return [`${Number(value).toFixed(1)}%`, name as string];
    return [fmtNumero(Number(value)), name as string];
  };

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
        Intensidad de la operación
      </h2>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardContent className="p-4 pt-4">
            <h3 className="mb-3 text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              Producción por turno
            </h3>
            {turnoData.length === 0 ? (
              <p className="py-8 text-center text-sm text-fg-tertiary">Sin datos en el rango.</p>
            ) : (
              <>
                <div className="mb-2 flex items-center gap-3 text-[10.5px] text-fg-tertiary">
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block size-2.5 rounded-sm bg-info" /> Cantidad producida
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block h-0.5 w-4 rounded bg-ok" /> Cumplimiento vs. meta
                  </span>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <ComposedChart
                    data={turnoData}
                    margin={{ top: 16, right: 16, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="var(--color-border)"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="label"
                      stroke="var(--color-fg-tertiary)"
                      tick={{ fontSize: 11, fill: 'var(--color-fg-tertiary)' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      yAxisId="cantidad"
                      stroke="var(--color-fg-tertiary)"
                      tick={{ fontSize: 10.5, fill: 'var(--color-fg-tertiary)' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => fmtNumero(Number(v))}
                      width={48}
                    />
                    <YAxis
                      yAxisId="pct"
                      orientation="right"
                      domain={[0, 130]}
                      tickFormatter={(v) => `${v}%`}
                      tick={{ fontSize: 10.5, fill: 'var(--color-fg-tertiary)' }}
                      axisLine={false}
                      tickLine={false}
                      width={36}
                    />
                    <Tooltip
                      contentStyle={{
                        background: 'var(--color-surface-elevated)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      formatter={tooltipFormatter}
                    />
                    <Bar
                      yAxisId="cantidad"
                      dataKey="cantidad"
                      name="Cantidad producida"
                      fill="var(--color-info)"
                      fillOpacity={0.85}
                      radius={[3, 3, 0, 0]}
                    >
                      <LabelList
                        content={
                          <EtiquetaValor formatter={(v) => fmtNumero(v)} total={turnoData.length} />
                        }
                      />
                    </Bar>
                    <Line
                      yAxisId="pct"
                      type="monotone"
                      dataKey="cumplimiento_pct"
                      name="Cumplimiento vs. meta"
                      stroke="var(--color-ok)"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      connectNulls
                    >
                      <LabelList
                        content={
                          <EtiquetaValor
                            formatter={(v) => `${v.toFixed(0)}%`}
                            total={turnoData.length}
                            dy={-10}
                          />
                        }
                      />
                    </Line>
                  </ComposedChart>
                </ResponsiveContainer>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 pt-4">
            <h3 className="mb-3 text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              Perfil por hora
            </h3>
            {horaData.length === 0 ? (
              <p className="py-8 text-center text-sm text-fg-tertiary">Sin datos en el rango.</p>
            ) : (
              <>
                <div className="mb-2 flex items-center gap-3 text-[10.5px] text-fg-tertiary">
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block size-2.5 rounded-sm bg-info" /> Cantidad producida
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block h-0.5 w-4 rounded bg-ok" /> Cumplimiento vs. meta
                  </span>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <ComposedChart
                    data={horaData}
                    margin={{ top: 16, right: 16, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="var(--color-border)"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="label"
                      stroke="var(--color-fg-tertiary)"
                      tick={{ fontSize: 10, fill: 'var(--color-fg-tertiary)' }}
                      axisLine={false}
                      tickLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      yAxisId="cantidad"
                      stroke="var(--color-fg-tertiary)"
                      tick={{ fontSize: 10.5, fill: 'var(--color-fg-tertiary)' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => fmtNumero(Number(v))}
                      width={48}
                    />
                    <YAxis
                      yAxisId="pct"
                      orientation="right"
                      domain={[0, 130]}
                      tickFormatter={(v) => `${v}%`}
                      tick={{ fontSize: 10.5, fill: 'var(--color-fg-tertiary)' }}
                      axisLine={false}
                      tickLine={false}
                      width={36}
                    />
                    <Tooltip
                      contentStyle={{
                        background: 'var(--color-surface-elevated)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      formatter={tooltipFormatter}
                    />
                    <Bar
                      yAxisId="cantidad"
                      dataKey="cantidad"
                      name="Cantidad producida"
                      fill="var(--color-info)"
                      fillOpacity={0.85}
                      radius={[3, 3, 0, 0]}
                    >
                      <LabelList
                        content={
                          <EtiquetaValor
                            formatter={(v) => fmtNumero(v)}
                            paso={pasoEtiquetas(horaData.length, 8)}
                            total={horaData.length}
                            fontSize={9.5}
                          />
                        }
                      />
                    </Bar>
                    <Line
                      yAxisId="pct"
                      type="monotone"
                      dataKey="cumplimiento_pct"
                      name="Cumplimiento vs. meta"
                      stroke="var(--color-ok)"
                      strokeWidth={2}
                      dot={{ r: 2 }}
                      connectNulls
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

function ThSort({
  active,
  dir,
  onClick,
  align = 'left',
  hint,
  children,
}: {
  active: boolean;
  dir: SortDir;
  onClick: () => void;
  align?: 'left' | 'right';
  /** Explicación llana de la columna (ⓘ al lado del título, fuera del botón de sort). */
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <TH align={align}>
      {/* El ⓘ vive como hermano del botón de sort (no anidado): su click no
          dispara el ordenamiento porque es un <button> aparte. */}
      <span
        className={cn(
          'inline-flex items-center gap-1',
          align === 'right' ? 'flex-row-reverse' : '',
        )}
      >
        <button
          type="button"
          onClick={onClick}
          className={cn(
            'inline-flex items-center gap-0.5 text-[10px] font-medium uppercase tracking-wider hover:text-fg',
            active ? 'text-fg' : 'text-fg-tertiary',
          )}
        >
          {children}
          {active ? <span className="text-[10px]">{dir === 'asc' ? '↑' : '↓'}</span> : null}
        </button>
        {hint ? <InfoHint>{hint}</InfoHint> : null}
      </span>
    </TH>
  );
}

function PctCell({ pct }: { pct: number | null }) {
  const tone = semaforoCumplimiento(pct);
  return (
    <TD align="right" numeric>
      <span className={cn('font-medium', toneTextClass(tone))}>{fmtPct(pct)}</span>
    </TD>
  );
}

function FilaLinea({
  fila,
  agrupacion,
  onClickLinea,
}: {
  fila: CumplimientoFila;
  agrupacion: Agrupacion;
  onClickLinea: (lineaId: string) => void;
}) {
  const toneMeta = semaforoCumplimiento(fila.cumplimiento_meta_pct);
  // Fila sintética "Tiendas": su programado se deriva (:= producido, interino) y
  // no tiene detalle de línea → no navega.
  const esTiendas = fila.origen_programa === 'autogenerado';
  const clickable = !esTiendas && agrupacion !== 'dia' && agrupacion !== 'area';

  if (agrupacion === 'area') {
    return (
      <TR>
        <TD>
          <span className="font-medium text-fg">{fila.area_nombre ?? '—'}</span>
        </TD>
        <TD align="right" numeric>
          {fmtNumero(fila.cantidad_real)}
        </TD>
        <TD align="right" numeric className="text-fg-secondary">
          {fmtNumero(fila.cantidad_programa)}
        </TD>
        <PctCellProg pct={fila.cumplimiento_pct} />
        <TD align="right" numeric className="text-fg-secondary">
          {fmtNumero(fila.producido_fuera_plan)}
        </TD>
        <PctCell pct={fila.cumplimiento_meta_pct} />
      </TR>
    );
  }

  if (agrupacion === 'dia') {
    return (
      <TR>
        <TD>
          <span className="font-medium text-fg">{fmtFechaCorta(fila.periodo)}</span>
          {esTiendas ? (
            <span className="ml-1.5 text-[10.5px] text-fg-tertiary">· Tiendas</span>
          ) : null}
        </TD>
        <TD align="right" numeric>
          {fmtNumero(fila.cantidad_real)}
        </TD>
        <TD align="right" numeric className="text-fg-secondary">
          {fmtNumero(fila.meta_acumulada)}
        </TD>
        <PctCell pct={fila.cumplimiento_meta_pct} />
        <TD align="right" numeric className="text-fg-secondary">
          {fmtNumero(fila.cantidad_programa)}
        </TD>
        <PctCellProg pct={fila.cumplimiento_pct} />
        <TD align="right" numeric className="text-fg-secondary">
          {fmtHoras(fila.horas_paro)}
        </TD>
        <TD align="right" numeric className="text-fg-secondary">
          {fila.lineas_count ?? '—'}
        </TD>
        <TD className="text-fg-secondary">{fila.top_causal_nombre ?? '—'}</TD>
      </TR>
    );
  }

  if (agrupacion === 'sku') {
    return (
      <TR
        className={cn(clickable && 'cursor-pointer hover:bg-surface-overlay/30')}
        onClick={clickable ? () => onClickLinea(fila.linea_id) : undefined}
      >
        <TD>
          <div className="flex items-center gap-1.5">
            <span
              aria-hidden
              className={cn('inline-block size-1.5 rounded-full', toneDotClass(toneMeta))}
            />
            <span className="font-medium text-fg">{fila.sku_codigo ?? '—'}</span>
            {fila.sku_descripcion ? (
              <span className="text-[10.5px] text-fg-tertiary">- {fila.sku_descripcion}</span>
            ) : null}
          </div>
          <div className="text-[10.5px] text-fg-tertiary">{fila.linea_codigo}</div>
        </TD>
        <TD align="right" numeric>
          {fmtNumero(fila.cantidad_real)}
        </TD>
        <TD align="right" numeric className="text-fg-secondary">
          {fmtNumero(fila.meta_acumulada)}
        </TD>
        <PctCell pct={fila.cumplimiento_meta_pct} />
        <TD align="right" numeric className="text-fg-secondary">
          {fmtNumero(fila.cantidad_programa)}
        </TD>
        <PctCellProg pct={fila.cumplimiento_pct} />
        <TD align="right" numeric className="text-fg-secondary">
          {fila.lineas_count ?? '—'}
        </TD>
      </TR>
    );
  }

  // Por línea
  return (
    <TR
      className={cn(clickable && 'cursor-pointer hover:bg-surface-overlay/30')}
      onClick={clickable ? () => onClickLinea(fila.linea_id) : undefined}
    >
      <TD>
        <div className="flex items-center gap-1.5">
          <span
            aria-hidden
            className={cn('inline-block size-1.5 rounded-full', toneDotClass(toneMeta))}
          />
          <span className="font-medium text-fg">{fila.linea_codigo}</span>
        </div>
        <div className="text-[10.5px] text-fg-tertiary">
          {esTiendas
            ? 'Corridas de tiendas · programado = producido (interino)'
            : fila.planta_nombre}
        </div>
      </TD>
      <TD align="right" numeric>
        {fmtNumero(fila.cantidad_real)}
      </TD>
      <TD align="right" numeric className="text-fg-secondary">
        {fmtNumero(fila.meta_acumulada)}
      </TD>
      <PctCell pct={fila.cumplimiento_meta_pct} />
      <TD align="right" numeric className="text-fg-secondary">
        {fmtHoras(fila.horas_trabajadas)}
      </TD>
      <TD align="right" numeric className="text-fg-secondary">
        {fmtHoras(fila.horas_paro)}
      </TD>
      <TD className="text-fg-secondary">{fila.top_causal_nombre ?? '—'}</TD>
      <TD align="right">
        <span className="text-fg-tertiary">›</span>
      </TD>
    </TR>
  );
}

/** % prog coloreado con el mismo criterio de semáforo que % meta. */
function PctCellProg({ pct }: { pct: number | null }) {
  const tone = semaforoCumplimiento(pct);
  return (
    <TD align="right" numeric>
      <span className={toneTextClass(tone)}>{fmtPct(pct)}</span>
    </TD>
  );
}

// =============================================================================
// Rentabilidad (WS-5 B3) — toggle, tarjetas, banner y tabla de utilidad ($)
// =============================================================================

/** Conmutador Operación / Rentabilidad + leyenda de la vista de costos. */
function VistaToggle({ vista, onChange }: { vista: VistaC5; onChange: (v: VistaC5) => void }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <SegmentedControl<VistaC5>
        value={vista}
        onChange={onChange}
        options={[
          { value: 'operacion', label: 'Operación' },
          { value: 'rentabilidad', label: 'Rentabilidad' },
        ]}
      />
      {vista === 'rentabilidad' ? (
        <p className="text-[11px] text-fg-tertiary">
          Valor de venta, costo y utilidad en pesos. Consolida líneas y tiendas; en tiendas los
          insumos (sticker y termo) se cobran adicional al cliente y suman al valor.
        </p>
      ) : null}
    </div>
  );
}

/** Etiqueta de la dimensión de una fila según la agrupación. */
function dimLabel(fila: CumplimientoFila, agrupacion: Agrupacion): string {
  if (agrupacion === 'area') return fila.area_nombre ?? '—';
  if (agrupacion === 'dia') return fmtFechaCorta(fila.periodo);
  if (agrupacion === 'sku') return fila.sku_codigo ?? '—';
  return fila.linea_codigo || '—';
}

/** Sub-renglón (planta o descripción) bajo la dimensión, si aplica. */
function dimSub(fila: CumplimientoFila, agrupacion: Agrupacion): string | null {
  if (agrupacion === 'linea') return fila.planta_nombre || null;
  if (agrupacion === 'sku') return fila.sku_descripcion ?? null;
  return null;
}

function KpiCardsRentabilidad({
  data,
}: {
  data: NonNullable<ReturnType<typeof useCoordinadorCumplimiento>['data']>;
}) {
  const t = data.totales;
  // Consolidación líneas + tiendas: la plata de la planta en una sola vista. Si
  // rentabilidad_tiendas es null (filtro por área/línea/cliente, o sin costos),
  // los cards muestran solo líneas.
  const rt = data.rentabilidad_tiendas ?? null;
  const valorL = t.valor_producido_total ?? 0;
  const costoL = t.costo_mano_obra_total ?? 0;
  const utilL = t.utilidad_total ?? 0;
  const valorT = rt?.total.valor_producido ?? 0;
  const costoT = rt?.total.costo_mano_obra ?? 0;
  const utilT = rt?.total.utilidad ?? 0;
  const valor = valorL + valorT;
  const costo = costoL + costoT;
  const utilidad = utilL + utilT;
  const pct = valor > 0 ? Number(((utilidad / valor) * 100).toFixed(2)) : null;
  const consolida = rt !== null;
  const breakdown = (l: number, tt: number) =>
    `Líneas ${fmtCopCompacto(l)} · Tiendas ${fmtCopCompacto(tt)}`;
  // DN9: lo sin tarifa no suma ni resta; se declara con su tamaño.
  const stL = t.sin_tarifa_total ?? null;
  const stT = rt?.total.sin_tarifa ?? null;
  const stPaq = (stL?.paquetes ?? 0) + (stT?.paquetes ?? 0);
  const stSkus = (stL?.skus ?? 0) + (stT?.skus ?? 0);
  const stMo = (stL?.costo_mano_obra ?? 0) + (stT?.costo_mano_obra ?? 0);
  const smL = t.sin_costo_mo_total ?? null;
  const smT = rt?.total.sin_costo_mo ?? null;
  const smPaq = (smL?.paquetes ?? 0) + (smT?.paquetes ?? 0);
  const smValor = (smL?.valor_producido ?? 0) + (smT?.valor_producido ?? 0);
  const pend = t.pendiente_validacion_utilidad ?? null;
  const baseLabel = t.base_utilidad === 'validada' ? 'producción validada' : 'producción capturada';
  return (
    <section className="flex flex-col gap-2">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Valor de venta"
          hint={`Suma de (paquetes × precio vigente del SKU) sobre ${baseLabel}, solo producto con tarifa. Consolida líneas y reempaque de tiendas; en tiendas incluye los insumos (sticker y termo) que se cobran adicional al cliente. Misma fórmula que el dashboard del directivo.`}
          value={fmtCopCompacto(valor)}
          tone="neutral"
          sub={consolida ? breakdown(valorL, valorT) : `${baseLabel} × precio`}
        />
        <KpiCard
          label="Costo"
          hint="Costo de mano de obra = operarios × horas REALES del bloque × costo por hora-persona vigente. Solo bloques con tarifa (la MO de producto sin precio se declara aparte, no resta)."
          value={fmtCopCompacto(costo)}
          tone="neutral"
          sub="mano de obra · horas reales"
        />
        <KpiCard
          label="Utilidad"
          hint="Valor de venta menos costo. Negativa (en rojo) significa que el costo superó lo facturado. Consolida líneas y tiendas."
          value={fmtCopCompacto(utilidad)}
          tone={utilidad < 0 ? 'crit' : 'ok'}
          sub={consolida ? breakdown(utilL, utilT) : 'venta − costo'}
        />
        <KpiCard
          label="% Utilidad"
          hint="Utilidad ÷ valor de venta. Margen sobre lo facturado en el período."
          value={fmtPct(pct)}
          tone={pct !== null && pct < 0 ? 'crit' : 'ok'}
          sub="utilidad ÷ valor de venta"
        />
      </div>
      <p className="text-[11px] text-fg-tertiary">
        Base: {baseLabel}.
        {pend
          ? ` Pendiente de validar: ${fmtNumero(pend.paquetes)} paq → ${fmtCopCompacto(pend.valor_producido)} de valor y ${fmtCopCompacto(pend.costo_mano_obra)} de MO (validado + pendiente = lo que ve el directivo).`
          : ''}{' '}
        {stPaq > 0 || smPaq > 0 ? (
          <>
            <span className="text-fg-secondary">
              Fuera del cálculo:
              {stPaq > 0
                ? ` sin tarifa ${fmtNumero(stPaq)} paq de ${stSkus} SKU(s) con ${fmtCopCompacto(stMo)} de mano de obra sin asignar`
                : ''}
              {stPaq > 0 && smPaq > 0 ? ' ·' : ''}
              {smPaq > 0
                ? ` sin costo hora-persona ${fmtNumero(smPaq)} paq por ${fmtCopCompacto(smValor)} de valor`
                : ''}
              .
            </span>{' '}
            Cargue precios (Configuración → SKUs) o el costo de mano de obra para incluirlos; no se
            muestran como pérdida ni como utilidad.
          </>
        ) : (
          'Toda la producción del período tiene precio y costo de mano de obra vigentes.'
        )}
      </p>
    </section>
  );
}

/** Banner de la peor fila con pérdida (utilidad < 0), si la hay. */
function BannerPerdida({
  data,
  agrupacion,
}: {
  data: NonNullable<ReturnType<typeof useCoordinadorCumplimiento>['data']>;
  agrupacion: Agrupacion;
}) {
  const conPerdida = data.filas.filter((f) => (f.utilidad ?? 0) < 0);
  if (conPerdida.length === 0) return null;
  const peor = conPerdida.reduce((a, b) => ((a.utilidad ?? 0) <= (b.utilidad ?? 0) ? a : b));
  const otras = conPerdida.length - 1;
  return (
    <div className="flex items-start gap-2 rounded-md border border-crit/30 bg-crit/5 px-3 py-2 text-[12px]">
      <span aria-hidden className="mt-px text-crit">
        ⚠
      </span>
      <p className="text-fg-secondary">
        <span className="font-medium text-fg">{dimLabel(peor, agrupacion)}</span> opera a pérdida:{' '}
        <span className="font-medium text-crit tabular-nums">
          {fmtCop(peor.utilidad ?? 0)} ({fmtPct(peor.utilidad_pct ?? null)})
        </span>{' '}
        — el costo de mano de obra supera el valor de venta.
        {otras > 0 ? (
          <span className="text-fg-tertiary">
            {' '}
            Hay {otras} {otras === 1 ? 'fila más' : 'filas más'} con pérdida.
          </span>
        ) : null}
      </p>
    </div>
  );
}

type SortRentKey =
  | 'dim'
  | 'producido'
  | 'precio'
  | 'valor'
  | 'costo'
  | 'utilidad'
  | 'util_pct'
  | 'util_paq';

/** Utilidad por paquete producido (utilidad ÷ producido). null si no produjo. */
function utilPaq(f: CumplimientoFila): number | null {
  if (!f.cantidad_real || f.cantidad_real <= 0) return null;
  return (f.utilidad ?? 0) / f.cantidad_real;
}

/** Precio unitario efectivo (valor de venta ÷ producido). null si no produjo. */
function precioUnit(f: CumplimientoFila): number | null {
  if (!f.cantidad_real || f.cantidad_real <= 0) return null;
  return (f.valor_producido ?? 0) / f.cantidad_real;
}

function TablaRentabilidad({
  data,
  agrupacion,
  onAgrupacion,
}: {
  data: NonNullable<ReturnType<typeof useCoordinadorCumplimiento>['data']>;
  agrupacion: Agrupacion;
  onAgrupacion: (a: Agrupacion) => void;
}) {
  const navigate = useNavigate();
  const agrupacionActual: Agrupacion = data.filtros.agrupacion;
  const [sortKey, setSortKey] = useState<SortRentKey>('utilidad');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Las filas de TIENDAS (cumplimiento) NO entran a Rentabilidad: su plata real
  // vive en TablaRentabilidadTiendas — acá aparecerían duplicadas y en $0.
  // Cubre pre-flip (fila sintética origen_programa) y post-flip (área "Tiendas"
  // real / tienda-líneas TND-*). El per-unidad del Total usa SOLO unidades de
  // líneas (el numerador de plata es solo-líneas; dividir por líneas+tiendas
  // diluiría la economía por paquete).
  const filasRent = useMemo(
    () =>
      data.filas.filter(
        (f) =>
          f.origen_programa !== 'autogenerado' &&
          f.area_nombre !== 'Tiendas' &&
          !f.linea_codigo.startsWith('TND-'),
      ),
    [data.filas],
  );
  const cantidadLineasTotal = useMemo(
    () => filasRent.reduce((s, f) => s + f.cantidad_real, 0),
    [filasRent],
  );

  const valorOrden = (f: CumplimientoFila, key: SortRentKey): number | string | null => {
    switch (key) {
      case 'dim':
        return dimLabel(f, agrupacionActual);
      case 'producido':
        return f.cantidad_real;
      case 'precio':
        return precioUnit(f);
      case 'valor':
        return f.valor_producido ?? null;
      case 'costo':
        return f.costo_mano_obra ?? null;
      case 'utilidad':
        return f.utilidad ?? null;
      case 'util_pct':
        return f.utilidad_pct ?? null;
      case 'util_paq':
        return utilPaq(f);
      default:
        return null;
    }
  };

  const filasOrdenadas = useMemo(() => {
    const sorted = [...filasRent];
    sorted.sort((a, b) => {
      const av = valorOrden(a, sortKey);
      const bv = valorOrden(b, sortKey);
      const aNull = av === null;
      const bNull = bv === null;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      let diff: number;
      if (typeof av === 'string' && typeof bv === 'string') diff = av.localeCompare(bv, 'es');
      else diff = (av as number) - (bv as number);
      return sortDir === 'asc' ? diff : -diff;
    });
    return sorted;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filasRent, sortKey, sortDir, agrupacionActual]);

  const toggleSort = (key: SortRentKey) => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'dim' ? 'asc' : 'desc');
    }
  };

  const onClickLinea = (lid: string) => {
    const params = new URLSearchParams();
    if (data.filtros.desde) params.set('desde', data.filtros.desde);
    if (data.filtros.hasta) params.set('hasta', data.filtros.hasta);
    if (data.filtros.granularidad) params.set('granularidad', data.filtros.granularidad);
    const qs = params.toString();
    navigate(`/coordinador/linea/${lid}${qs ? `?${qs}` : ''}`);
  };

  const dimHeader =
    agrupacionActual === 'area'
      ? 'Área'
      : agrupacionActual === 'linea'
        ? 'Línea'
        : agrupacionActual === 'sku'
          ? 'SKU'
          : 'Día';

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <h2 className="text-sm font-medium text-fg">Rentabilidad</h2>
            <p className="text-[11px] text-fg-tertiary">
              <span className="tabular-nums">{data.filas.length}</span> {dimHeader.toLowerCase()}
              {data.filas.length === 1 ? '' : 's'}
            </p>
          </div>
          <SegmentedControl<Agrupacion>
            size="sm"
            value={agrupacion}
            onChange={onAgrupacion}
            options={[
              { value: 'area', label: 'Por área' },
              { value: 'linea', label: 'Por línea' },
              { value: 'dia', label: 'Por día' },
              { value: 'sku', label: 'Por SKU' },
            ]}
          />
        </div>

        {filasOrdenadas.length === 0 ? (
          <p className="py-4 text-center text-sm text-fg-tertiary">
            Sin datos en el período seleccionado.
          </p>
        ) : (
          <Table maxHeight="70vh">
            <THead sticky>
              <TR>
                <ThSort active={sortKey === 'dim'} dir={sortDir} onClick={() => toggleSort('dim')}>
                  {dimHeader}
                </ThSort>
                <ThSort
                  active={sortKey === 'producido'}
                  dir={sortDir}
                  onClick={() => toggleSort('producido')}
                  align="right"
                  hint={HINT_COL.producido}
                >
                  Producido
                </ThSort>
                {agrupacionActual === 'sku' ? (
                  <ThSort
                    active={sortKey === 'precio'}
                    dir={sortDir}
                    onClick={() => toggleSort('precio')}
                    align="right"
                    hint="Precio unitario vigente del SKU (valor de venta ÷ producido)."
                  >
                    Precio
                  </ThSort>
                ) : null}
                <ThSort
                  active={sortKey === 'valor'}
                  dir={sortDir}
                  onClick={() => toggleSort('valor')}
                  align="right"
                  hint="Paquetes producidos × precio vigente del SKU."
                >
                  Valor de venta
                </ThSort>
                <ThSort
                  active={sortKey === 'costo'}
                  dir={sortDir}
                  onClick={() => toggleSort('costo')}
                  align="right"
                  hint="Costo de mano de obra: operarios × horas × costo por hora-persona."
                >
                  Costo
                </ThSort>
                <ThSort
                  active={sortKey === 'utilidad'}
                  dir={sortDir}
                  onClick={() => toggleSort('utilidad')}
                  align="right"
                  hint="Valor de venta menos costo de mano de obra."
                >
                  Utilidad
                </ThSort>
                <ThSort
                  active={sortKey === 'util_pct'}
                  dir={sortDir}
                  onClick={() => toggleSort('util_pct')}
                  align="right"
                  hint="Utilidad ÷ valor de venta."
                >
                  % Utilidad
                </ThSort>
                <ThSort
                  active={sortKey === 'util_paq'}
                  dir={sortDir}
                  onClick={() => toggleSort('util_paq')}
                  align="right"
                  hint="Utilidad por paquete producido."
                >
                  Utilidad/paq
                </ThSort>
              </TR>
            </THead>
            <TBody>
              {filasOrdenadas.map((f, i) => (
                <FilaRentabilidad
                  key={`${f.periodo}-${f.linea_id}-${f.area_id ?? ''}-${f.sku_id ?? ''}-${i}`}
                  fila={f}
                  agrupacion={agrupacionActual}
                  onClickLinea={onClickLinea}
                />
              ))}
            </TBody>
            <TFoot>
              <TR>
                <TD>Total</TD>
                <TD align="right" numeric>
                  {fmtNumero(cantidadLineasTotal)}
                </TD>
                {agrupacionActual === 'sku' ? (
                  <TD align="right" numeric>
                    {cantidadLineasTotal > 0
                      ? fmtCop((data.totales.valor_producido_total ?? 0) / cantidadLineasTotal)
                      : '—'}
                  </TD>
                ) : null}
                <TD align="right" numeric>
                  {fmtCop(data.totales.valor_producido_total ?? 0)}
                </TD>
                <TD align="right" numeric>
                  {fmtCop(data.totales.costo_mano_obra_total ?? 0)}
                </TD>
                <TD
                  align="right"
                  numeric
                  className={cn((data.totales.utilidad_total ?? 0) < 0 && 'text-crit')}
                >
                  {fmtCop(data.totales.utilidad_total ?? 0)}
                </TD>
                <TD
                  align="right"
                  numeric
                  className={cn(
                    (data.totales.utilidad_pct_total ?? 0) < 0 ? 'text-crit' : undefined,
                  )}
                >
                  {fmtPct(data.totales.utilidad_pct_total ?? null)}
                </TD>
                <TD align="right" numeric>
                  {cantidadLineasTotal > 0
                    ? fmtCop((data.totales.utilidad_total ?? 0) / cantidadLineasTotal)
                    : '—'}
                </TD>
              </TR>
            </TFoot>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

/** Celda monetaria: negativos en rojo; la columna fuerte usa color de texto pleno. */
// =============================================================================
// Rentabilidad de tiendas (WS-5) — sección consolidada dentro de la vista
// Rentabilidad. Reempaque por destino: valor unidades + insumos cobrados al
// cliente (sticker/termo SUMAN, no restan), costo de mano de obra y utilidad.
// Solo aparece si el endpoint devolvió rentabilidad_tiendas (puede ver
// costos y sin filtro de área/línea/cliente).
// =============================================================================

function TablaRentabilidadTiendas({
  data,
}: {
  data: NonNullable<ReturnType<typeof useCoordinadorCumplimiento>['data']>;
}) {
  const rt = data.rentabilidad_tiendas ?? null;
  if (!rt) return null;
  const destinos = rt.por_destino;
  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <h2 className="text-sm font-medium text-fg">Rentabilidad de tiendas</h2>
            <p className="text-[11px] text-fg-tertiary">
              Reempaque por destino · <span className="tabular-nums">{destinos.length}</span>{' '}
              destino
              {destinos.length === 1 ? '' : 's'} · los insumos (sticker y termo) se cobran adicional
              al cliente
            </p>
          </div>
        </div>

        {destinos.length === 0 ? (
          <p className="py-4 text-center text-sm text-fg-tertiary">
            Sin corridas de tienda en el período seleccionado.
          </p>
        ) : (
          <Table maxHeight="60vh">
            <THead sticky>
              <TR>
                <TH>Destino</TH>
                <TH align="right" title="Unidades reempacadas × precio vigente del SKU.">
                  Valor unidades
                </TH>
                <TH
                  align="right"
                  title="Stickers × tarifa del sticker + termos × tarifa del termo — se cobran adicional al cliente."
                >
                  Insumos cobrados
                </TH>
                <TH
                  align="right"
                  title="Valor unidades + insumos cobrados: total cobrado al cliente."
                >
                  Valor de venta
                </TH>
                <TH
                  align="right"
                  title="Operarios × duración de la corrida × costo por hora-persona."
                >
                  Costo mano de obra
                </TH>
                <TH align="right" title="Valor de venta menos costo de mano de obra.">
                  Utilidad
                </TH>
                <TH align="right" title="Utilidad ÷ valor de venta.">
                  % Utilidad
                </TH>
              </TR>
            </THead>
            <TBody>
              {destinos.map((d) => {
                const neg = d.utilidad < 0;
                return (
                  <TR key={d.tienda_id}>
                    <TD>
                      <div className="flex items-center gap-1.5">
                        {neg ? (
                          <span
                            aria-hidden
                            className="inline-block size-1.5 rounded-full bg-crit"
                          />
                        ) : null}
                        <span className="font-medium text-fg">{d.tienda_nombre}</span>
                      </div>
                    </TD>
                    <MoneyCell n={d.valor_unidades} />
                    <MoneyCell n={d.ingreso_insumos} />
                    <MoneyCell n={d.valor_producido} />
                    <MoneyCell n={d.costo_mano_obra} />
                    <MoneyCell n={d.utilidad} strong />
                    <TD align="right" numeric className={cn(neg ? 'text-crit' : 'text-fg')}>
                      {fmtPct(d.utilidad_pct)}
                    </TD>
                  </TR>
                );
              })}
            </TBody>
            <TFoot>
              <TR>
                <TD>Total tiendas</TD>
                <MoneyCell n={rt.total.valor_unidades} strong />
                <MoneyCell n={rt.total.ingreso_insumos} strong />
                <MoneyCell n={rt.total.valor_producido} strong />
                <MoneyCell n={rt.total.costo_mano_obra} strong />
                <MoneyCell n={rt.total.utilidad} strong />
                <TD
                  align="right"
                  numeric
                  className={cn(rt.total.utilidad < 0 ? 'text-crit' : 'text-fg')}
                >
                  {fmtPct(rt.total.utilidad_pct)}
                </TD>
              </TR>
            </TFoot>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function MoneyCell({ n, strong }: { n: number | null; strong?: boolean }) {
  const neg = (n ?? 0) < 0;
  return (
    <TD
      align="right"
      numeric
      className={cn(neg ? 'text-crit' : strong ? 'text-fg' : 'text-fg-secondary')}
    >
      {fmtCop(n)}
    </TD>
  );
}

function FilaRentabilidad({
  fila,
  agrupacion,
  onClickLinea,
}: {
  fila: CumplimientoFila;
  agrupacion: Agrupacion;
  onClickLinea: (lineaId: string) => void;
}) {
  const clickable = agrupacion === 'linea' || agrupacion === 'sku';
  const sub = dimSub(fila, agrupacion);
  const negUtil = (fila.utilidad ?? 0) < 0;
  const pq = utilPaq(fila);
  return (
    <TR
      className={cn(clickable && 'cursor-pointer hover:bg-surface-overlay/30')}
      onClick={clickable ? () => onClickLinea(fila.linea_id) : undefined}
    >
      <TD>
        <div className="flex items-center gap-1.5">
          {negUtil ? (
            <span aria-hidden className="inline-block size-1.5 rounded-full bg-crit" />
          ) : null}
          <span className="font-medium text-fg">{dimLabel(fila, agrupacion)}</span>
        </div>
        {sub ? <div className="text-[10.5px] text-fg-tertiary">{sub}</div> : null}
      </TD>
      <TD align="right" numeric className="text-fg-secondary">
        {fmtNumero(fila.cantidad_real)}
      </TD>
      {agrupacion === 'sku' ? (
        <TD align="right" numeric className="text-fg-secondary">
          {precioUnit(fila) !== null ? fmtCop(precioUnit(fila)) : '—'}
        </TD>
      ) : null}
      <MoneyCell n={fila.valor_producido ?? 0} />
      <MoneyCell n={fila.costo_mano_obra ?? 0} />
      <MoneyCell n={fila.utilidad ?? 0} strong />
      <TD align="right" numeric className={cn(negUtil ? 'text-crit' : 'text-fg')}>
        {fmtPct(fila.utilidad_pct ?? null)}
      </TD>
      <TD
        align="right"
        numeric
        className={cn(pq !== null && pq < 0 ? 'text-crit' : 'text-fg-secondary')}
      >
        {pq !== null ? fmtCop(pq) : '—'}
      </TD>
    </TR>
  );
}

// =============================================================================
// Gráficos de rentabilidad (WS-5 B3)
// =============================================================================

/** Etiqueta corta del período según la granularidad. */
function labelPeriodo(periodo: string, gran: Granularidad): string {
  if (gran === 'dia') return fmtFechaCorta(periodo);
  if (gran === 'semana') return periodo.replace(/^\d{4}-/, ''); // "W22"
  const [y, m] = periodo.split('-').map(Number); // mes: YYYY-MM
  if (!y || !m) return periodo;
  return new Intl.DateTimeFormat('es-CO', { month: 'short', timeZone: 'UTC' })
    .format(new Date(Date.UTC(y, m - 1, 1)))
    .replace('.', '');
}

/** Rentabilidad en el tiempo: barras Valor de venta + Costo y línea de % Utilidad. */
function GraficoRentabilidadTiempo({
  data,
  granRent,
  onGranRent,
}: {
  data: NonNullable<ReturnType<typeof useCoordinadorCumplimiento>['data']>;
  granRent: Granularidad;
  onGranRent: (g: Granularidad) => void;
}) {
  const chartData = data.serie_temporal.map((s) => ({
    label: labelPeriodo(s.periodo, granRent),
    valor: s.valor_producido ?? 0,
    costo: s.costo_mano_obra ?? 0,
    pct: s.utilidad_pct ?? null,
  }));

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-medium text-fg">Rentabilidad en el tiempo</h2>
          <SegmentedControl<Granularidad>
            size="sm"
            value={granRent}
            onChange={onGranRent}
            options={[
              { value: 'dia', label: 'Día' },
              { value: 'semana', label: 'Semana' },
              { value: 'mes', label: 'Mes' },
            ]}
          />
        </div>

        {/* Leyenda manual */}
        <div className="flex flex-wrap items-center gap-4 text-[10.5px] text-fg-tertiary">
          <span className="flex items-center gap-1.5">
            <span className="inline-block size-2.5 rounded-sm bg-accent" /> Valor de venta
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block size-2.5 rounded-sm bg-fg-tertiary" /> Costo
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-0.5 w-4 rounded bg-fg-secondary" /> % Utilidad
          </span>
        </div>

        {chartData.length === 0 ? (
          <p className="py-8 text-center text-sm text-fg-tertiary">Sin datos para el gráfico.</p>
        ) : (
          <div className="h-[280px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 16, right: 8, left: 8, bottom: 8 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="var(--color-border)"
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10.5, fill: 'var(--color-fg-tertiary)' }}
                  axisLine={{ stroke: 'var(--color-border)' }}
                  tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  yAxisId="pesos"
                  tickFormatter={(v) => fmtCopCompacto(Number(v))}
                  tick={{ fontSize: 10.5, fill: 'var(--color-fg-tertiary)' }}
                  axisLine={false}
                  tickLine={false}
                  width={56}
                />
                <YAxis
                  yAxisId="pct"
                  orientation="right"
                  tickFormatter={(v) => `${Number(v).toFixed(0)}%`}
                  tick={{ fontSize: 10.5, fill: 'var(--color-fg-tertiary)' }}
                  axisLine={false}
                  tickLine={false}
                  width={40}
                />
                <Tooltip
                  contentStyle={{
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  formatter={(value, name) => {
                    if (value === null || value === undefined) return ['s/d', name as string];
                    if (name === '% Utilidad') return [`${Number(value).toFixed(1)}%`, name];
                    return [fmtCop(Number(value)), name as string];
                  }}
                />
                <Bar
                  yAxisId="pesos"
                  dataKey="valor"
                  name="Valor de venta"
                  fill="var(--color-accent)"
                  fillOpacity={0.85}
                  radius={[3, 3, 0, 0]}
                />
                <Bar
                  yAxisId="pesos"
                  dataKey="costo"
                  name="Costo"
                  fill="var(--color-fg-tertiary)"
                  fillOpacity={0.6}
                  radius={[3, 3, 0, 0]}
                />
                <Line
                  yAxisId="pct"
                  type="monotone"
                  dataKey="pct"
                  name="% Utilidad"
                  stroke="var(--color-fg-secondary)"
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  connectNulls
                >
                  <LabelList
                    content={
                      <EtiquetaValor
                        formatter={(v) => `${v.toFixed(0)}%`}
                        paso={pasoEtiquetas(chartData.length)}
                        total={chartData.length}
                        dy={-10}
                      />
                    }
                  />
                </Line>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Utilidad por área: barras ordenadas de mayor a menor; pérdidas en rojo. */
function GraficoUtilidadArea({
  data,
}: {
  data: NonNullable<ReturnType<typeof useCoordinadorCumplimiento>['data']>;
}) {
  const areas = data.utilidad_por_area ?? [];
  const chartData = areas.map((a) => ({ label: a.area_nombre, utilidad: a.utilidad }));

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-3">
        <div>
          <h2 className="text-sm font-medium text-fg">Utilidad por área</h2>
          <p className="text-[11px] text-fg-tertiary">
            Ordenado de mayor a menor · pérdidas en rojo bajo el eje cero
          </p>
        </div>

        {chartData.length === 0 ? (
          <p className="py-8 text-center text-sm text-fg-tertiary">Sin datos para el gráfico.</p>
        ) : (
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 20, right: 8, left: 8, bottom: 8 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="var(--color-border)"
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10.5, fill: 'var(--color-fg-tertiary)' }}
                  axisLine={{ stroke: 'var(--color-border)' }}
                  tickLine={false}
                  interval={0}
                />
                <YAxis
                  tickFormatter={(v) => fmtCopCompacto(Number(v))}
                  tick={{ fontSize: 10.5, fill: 'var(--color-fg-tertiary)' }}
                  axisLine={false}
                  tickLine={false}
                  width={56}
                />
                <Tooltip
                  cursor={{ fill: 'var(--color-surface-overlay)', fillOpacity: 0.4 }}
                  contentStyle={{
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  formatter={(value) => [fmtCop(Number(value)), 'Utilidad']}
                />
                <ReferenceLine y={0} stroke="var(--color-border-strong)" />
                <Bar dataKey="utilidad" name="Utilidad" radius={[3, 3, 0, 0]}>
                  {chartData.map((d, i) => (
                    <Cell
                      key={i}
                      fill={d.utilidad < 0 ? 'var(--color-crit)' : 'var(--color-accent)'}
                      fillOpacity={d.utilidad < 0 ? 0.9 : 0.85}
                    />
                  ))}
                  <LabelList
                    content={
                      <EtiquetaValor
                        formatter={(v) => fmtCopCompacto(v)}
                        paso={pasoEtiquetas(chartData.length)}
                        total={chartData.length}
                      />
                    }
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
